/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import ZoomInIcon from '@mui/icons-material/Add';
import ZoomOutIcon from '@mui/icons-material/Remove';
import ZoomOutMapRounded from '@mui/icons-material/ZoomOutMapRounded';
import { Button, ButtonGroup, Paper } from '@mui/material';
import type { FitBoundsOptions, LatLngBoundsExpression } from 'leaflet';
import { useCallback } from 'react';
import { useMap } from 'react-leaflet';

interface MapZoomControlsProps {
  center: [number, number] | undefined;
  defaultZoom: number;
  fullExtentBounds?: LatLngBoundsExpression;
  fullExtentFitBoundsOptions?: FitBoundsOptions;
  onZoomChange: (zoom: number) => void;
}

export default function MapZoomControls({
  center,
  defaultZoom,
  fullExtentBounds,
  fullExtentFitBoundsOptions,
  onZoomChange,
}: Readonly<MapZoomControlsProps>) {
  const map = useMap();

  const handleZoomIn = useCallback(() => {
    map.zoomIn();
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    map.zoomOut();
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  const handleResetZoom = useCallback(() => {
    if (fullExtentBounds) {
      map.fitBounds(fullExtentBounds, fullExtentFitBoundsOptions);
      onZoomChange(map.getZoom());
      return;
    }
    if (!center) return;
    map.setView({ lat: center[0], lng: center[1] }, defaultZoom);
    onZoomChange(defaultZoom);
  }, [map, fullExtentBounds, fullExtentFitBoundsOptions, center, defaultZoom, onZoomChange]);

  return (
    <Paper elevation={3} sx={{ display: 'flex', flexDirection: 'column', opacity: 0.9 }}>
      <ButtonGroup
        orientation="vertical"
        size="small"
        sx={{
          textAlign: 'center',
          '& .MuiButton-root': {
            py: 0.5,
            px: 0,
            width: '35px',
            border: 'none',
            '&:not(:last-of-type)': {
              borderBottom: 'none',
            },
          },
        }}
      >
        <Button onClick={handleZoomIn} aria-label="Zoom in" sx={{ height: '35px' }}>
          <ZoomInIcon fontSize="small" />
        </Button>
        <Button onClick={handleZoomOut} aria-label="Zoom out" sx={{ height: '35px' }}>
          <ZoomOutIcon fontSize="small" />
        </Button>
        <Button
          onClick={handleResetZoom}
          disabled={!fullExtentBounds && !center}
          aria-label="Reset zoom"
          sx={{ height: '35px' }}
        >
          <ZoomOutMapRounded fontSize="small" />
        </Button>
      </ButtonGroup>
    </Paper>
  );
}
